import { FC, lazy, Suspense, useCallback, useMemo } from 'react';
import { BasketMeta, QuoteAddress, QuoteParcels } from '~/lib/types/basket-types';
import useBasketSection from '~/hooks/use-basket-section';
import { BasketSections, RequirementTypes } from '~/lib/types/enums';
import Section from '~/components/core/section/section';
import Address from '~/components/address-details/address/address';
import { Trans, useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { BaseModuleProps } from './module-factory';
import useSignIn from '~/hooks/use-signin';
import { useAddressRequirements } from '~/hooks/use-address-requirements';
import useQuote from '~/hooks/use-quote';
import { P2GAlert, P2GBox, P2GText } from '@parcel2go/core-ui';
import { useAnalytics } from '@parcel2go/npm-p2g-app';
import useBasket from '~/hooks/use-basket';
import SendToMultiple from '~/components/address-details/address/send-to-multiple';

const CmsContent = lazy(() => import('~/components/core/content/cms-content'));

const DestinationModule: FC<BaseModuleProps> = ({ errors, wrapper }) => {
  const { onSignInClick } = useSignIn();
  const { t } = useTranslation([TranslationNamespaces.CHECKOUT]);
  const { quote } = useQuote();
  const { bulkUpdateSections } = useBasket();
  const { track } = useAnalytics();
  const parcels = useBasketSection<QuoteParcels>({ name: BasketSections.PARCELS });
  const destinationSection = useBasketSection<QuoteAddress>({ name: BasketSections.DESTINATION });
  const originSection = useBasketSection<QuoteAddress>({ name: BasketSections.ORIGIN });
  const metaSection = useBasketSection<BasketMeta>({ name: BasketSections.META });
  const { requirements, countries, loading, requiresCustoms, unfilteredRequirementKeys } = useAddressRequirements(
    RequirementTypes.DESTINATION
  );
  const extras = quote?.availableExtras?.map((x) => x.key) || [];

  const usableOriginDetails = useMemo(() => {
    const output: string[] = [];

    if (requiresCustoms) {
      return output;
    }

    if (
      !destinationSection.data.email &&
      originSection.data.email &&
      requirements.rules.some((r) => r.key === 'email' && r.required)
    ) {
      output.push('email');
    }

    if (
      !destinationSection.data.phone &&
      originSection.data.phone &&
      requirements.rules.some((r) => r.key === 'phone' && r.required)
    ) {
      output.push('phone');
    }

    return output;
  }, [destinationSection.data, originSection.data, requirements, requiresCustoms]);

  const onUseOriginDetails = useCallback(
    (field: string) => {
      switch (field) {
        case 'email':
          destinationSection.update({
            ...destinationSection.data,
            email: originSection.data.email
          });
          break;
        case 'phone':
          destinationSection.update({
            ...destinationSection.data,
            phone: originSection.data.phone
          });
          break;
      }
      track({ type: 'address_use_origin_contact_details', payload: { field } });
    },
    [originSection, destinationSection, track]
  );

  const onConfirmSendingToMultipleDestinations = useCallback(
    async (isSameContents: boolean) => {
      bulkUpdateSections({
        [BasketSections.PARCELS]: {
          ...parcels.data,
          parcels: parcels.data.parcels.filter((_x, i) => i === 0),
          splitParcels: parcels.data.parcels.filter((_x, i) => i > 0)
        } as QuoteParcels,
        [BasketSections.DESTINATION]: {
          ...destinationSection.data,
          metadata: {
            ...destinationSection.data.metadata,
            sendToMultiple: isSameContents ? 'split-same-contents' : 'split-different-contents'
          }
        } as QuoteAddress
      });
    },
    [bulkUpdateSections, destinationSection, parcels]
  );

  const onConfirmSendingToSingleDestination = useCallback(async () => {
    bulkUpdateSections({
      [BasketSections.PARCELS]: {
        ...parcels.data,
        parcels: parcels.data.parcels.concat(parcels.data.splitParcels),
        splitParcels: []
      } as QuoteParcels,
      [BasketSections.DESTINATION]: {
        ...destinationSection.data,
        metadata: {
          ...destinationSection.data.metadata,
          sendToMultiple: undefined
        }
      } as QuoteAddress
    });
  }, [bulkUpdateSections, destinationSection, parcels]);

  const showSendToMultiple =
    parcels.data.initialParcels && parcels.data.initialParcels > 1 && !metaSection.data.completed;

  const isSendToMultiple = !!destinationSection.data.metadata?.sendToMultiple && !metaSection.data.completed;

  if (!requirements?.rules?.length) return null;

  return wrapper(
    <Section
      title={t('DESTINATION_ADDRESS_TITLE', { defaultValue: 'Delivery Address' })}
      dataTestId="destination-address-section"
      loading={destinationSection.loading || loading}
      subTitle={
        showSendToMultiple && (
          <SendToMultiple
            origin={originSection.data.country || 'GBR'}
            destination={destinationSection.data.country || 'GBR'}
            onRevert={onConfirmSendingToSingleDestination}
            isSendToMultiple={!!destinationSection.data.metadata?.sendToMultiple}
            count={parcels.data.initialParcels ?? 0}
            onConfirm={onConfirmSendingToMultipleDestinations}
          />
        )
      }
    >
      <Address
        disabledFields={[]}
        unfilteredRequirementKeys={unfilteredRequirementKeys}
        signIn={onSignInClick}
        section={destinationSection}
        requirements={requirements}
        errors={errors}
        availableExtras={extras}
        type={BasketSections.DESTINATION}
        usableOriginDetails={usableOriginDetails}
        onUseOriginDetails={onUseOriginDetails}
        countries={countries}
        message={
          requiresCustoms ? (
            <Suspense>
              <CmsContent contentKey="customs-destination-intro-message">
                <P2GAlert type="info">
                  <P2GBox sx={(t) => ({ display: 'flex', gap: t.spacing(1), flexDirection: 'column' })}>
                    <P2GText variant="body1" fontWeight="bold" component="p">
                      {t('CUSTOMS_DESTINATION_INTRO_MESSAGE_TITLE', {
                        defaultValue: 'Ensure the Recipients Contact Information Is Correct'
                      })}
                    </P2GText>
                    <P2GText variant="body1" component="p">
                      {t('CUSTOMS_DESTINATION_INTRO_MESSAGE', {
                        defaultValue:
                          'Customs authorities may contact the recipient for parcel clearance. Ensure their contact info is correct to avoid delivery delays and extra charges.'
                      })}
                    </P2GText>
                  </P2GBox>
                </P2GAlert>
              </CmsContent>
            </Suspense>
          ) : isSendToMultiple ? (
            <P2GAlert type="warning">
              <P2GBox sx={(t) => ({ display: 'flex', gap: t.spacing(1), flexDirection: 'column' })}>
                <P2GText variant="body1" fontWeight="bold" component="p">
                  <Trans
                    i18nKey="MULTIDESTINATION_INTRO_MESSAGE_TITLE"
                    t={t}
                    defaults="Enter the delivery address for your first parcel (1 of {{count}})"
                    values={{ count: parcels?.data?.initialParcels ?? 0 }}
                  />
                </P2GText>
                <P2GText variant="body1" component="p">
                  {t('MULTIDESTINATION_INTRO_MESSAGE', {
                    defaultValue: `We’ll start by capturing the address for your first parcel. Don’t worry—if you’re sending to multiple addresses, we’ll collect the rest at the end of the order process.`
                  })}
                </P2GText>
              </P2GBox>
            </P2GAlert>
          ) : undefined
        }
      />
    </Section>
  );
};

export default DestinationModule;
